import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TablePagination,
  Alert,
  Chip,
} from '@mui/material';
import {
  RuleType,
  CustomerRules,
  PreviewLead,
  PreviewTimeframe,
  timeframeLabels,
  LeadStatus,
  leadStatusLabels,
} from '../../types/leadSourceRules';
import { postData } from '../../helpers/apiUtils';
import moment from 'moment';

interface LeadPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  ruleType: RuleType;
  customer: string;
  source: string;
  rules: CustomerRules | null;
  timeframe: PreviewTimeframe;
  leadStatus: LeadStatus;
}

export const LeadPreviewDialog: React.FC<LeadPreviewDialogProps> = ({
  open,
  onClose,
  ruleType,
  customer,
  source,
  rules,
  timeframe,
  leadStatus,
}) => {
  const [leads, setLeads] = useState<PreviewLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const loadLeads = useCallback(async () => {
    if (!rules) return;

    setLoading(true);
    setError(null);
    try {
      const response = await postData('/api/lead-source-rules/preview-leads', {
        rule_type: ruleType,
        customer,
        source,
        rules,
        timeframe,
        lead_status: leadStatus,
        page: page + 1,
        pageSize,
      });

      if (response.success && response.data) {
        const data = response.data as { leads: PreviewLead[]; pagination: { total: number } };
        setLeads(data.leads);
        setTotal(data.pagination.total);
      } else {
        setError(response.error || 'Failed to load leads');
      }
    } catch (err) {
      setError('Failed to load lead preview');
    } finally {
      setLoading(false);
    }
  }, [ruleType, customer, source, rules, timeframe, leadStatus, page, pageSize]);

  useEffect(() => {
    if (open) {
      loadLeads();
    }
  }, [open, loadLeads]);

  // Reset page when dialog opens
  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open]);

  const truncate = (text: string | null, maxLength = 30): string => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Lead Preview</Typography>
          <Chip label={`${customer.toUpperCase()} / ${source}`} size="small" variant="outlined" />
          <Chip
            label={timeframeLabels[timeframe]}
            size="small"
            color={timeframe === 'full' ? 'warning' : 'info'}
          />
          {leadStatus !== 'all' && (
            <Chip
              label={leadStatusLabels[leadStatus]}
              size="small"
              color={
                leadStatus === 'error' ? 'error' : leadStatus === 'new' ? 'success' : 'default'
              }
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : leads.length === 0 ? (
          <Alert severity="info">No leads match the current rules</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {leads.length} of {total.toLocaleString()} matching leads
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Process ID
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Confirmed
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Origin Host
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Source
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Affiliate
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                      Adref
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead, index) => (
                    <TableRow key={`${lead.process_id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {lead.process_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                          {truncate(lead.email, 40)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {lead.confirmed ? moment.utc(lead.confirmed).format('DD.MM.YY HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary">
                          {truncate(lead.origin_host, 25)}
                        </Typography>
                      </TableCell>
                      <TableCell>{truncate(lead.source, 20)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          fontSize="0.7rem"
                          sx={{ wordBreak: 'break-all', whiteSpace: 'normal', minWidth: 240 }}
                        >
                          {lead.clean_affiliate || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          fontSize="0.7rem"
                          sx={{ wordBreak: 'break-all', whiteSpace: 'normal', minWidth: 240 }}
                        >
                          {lead.adref || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
